import { ABANDON_AFTER_MS } from '@/config/learning';
import { curriculum } from '@/content/curriculum';
import type { GameId } from '@/domain/ids';
import {
  exerciseAttemptSchema,
  learningSessionSchema,
  type ExerciseAttempt,
  type LearnerAchievement,
  type LearningSession,
  type MasteryRecord,
} from '@/domain/records';
import { unlockedIds } from '@/features/achievements/catalog';
import type { AttemptDraft } from '@/features/games/engine';
import { computeMastery } from '@/features/mastery/model';
import { newId } from '@/lib/ids';
import { nowIso } from '@/lib/time';
import { db, safeWrite } from './db/database';

/**
 * Learning records service: sessions, attempts, the derived mastery cache and
 * achievements. Every query is scoped by learner id so profiles never mix.
 */

export interface StartSessionInput {
  learnerId: string;
  gameId: GameId;
  subjectId?: string;
  objectiveIds: string[];
  seed: number;
  questionCount: number;
  isReview?: boolean;
}

export async function startLearningSession(input: StartSessionInput): Promise<LearningSession> {
  const session = learningSessionSchema.parse({
    id: newId(),
    ...input,
    status: 'inProgress',
    startedAt: nowIso(),
  });
  await safeWrite(() => db.sessions.add(session));
  return session;
}

export async function recordAttempt(
  learnerId: string,
  sessionId: string,
  draft: AttemptDraft,
): Promise<ExerciseAttempt> {
  const attempt = exerciseAttemptSchema.parse({
    id: newId(),
    learnerId,
    sessionId,
    ...draft,
    answeredAt: nowIso(),
  });
  await safeWrite(() => db.attempts.add(attempt));
  return attempt;
}

/** Recomputes the mastery cache of some objectives from stored attempts. */
export async function refreshMastery(
  learnerId: string,
  objectiveIds: string[],
): Promise<MasteryRecord[]> {
  const records: MasteryRecord[] = [];
  for (const objectiveId of new Set(objectiveIds)) {
    const attempts = await db.attempts
      .where('[learnerId+objectiveId]')
      .equals([learnerId, objectiveId])
      .toArray();
    const estimate = computeMastery(attempts, curriculum.objective(objectiveId));
    records.push({ learnerId, objectiveId, ...estimate, updatedAt: nowIso() });
  }
  await safeWrite(() => db.mastery.bulkPut(records));
  return records;
}

/** Rebuilds every mastery record of a learner (after an import or a model change). */
export async function rebuildAllMastery(learnerId: string): Promise<void> {
  const objectiveIds = (await db.attempts.where('learnerId').equals(learnerId).toArray()).map(
    (a) => a.objectiveId,
  );
  await db.mastery.where('learnerId').equals(learnerId).delete();
  await refreshMastery(learnerId, objectiveIds);
}

export async function getMasteryMap(learnerId: string): Promise<Map<string, MasteryRecord>> {
  const records = await db.mastery.where('learnerId').equals(learnerId).toArray();
  return new Map(records.map((r) => [r.objectiveId, r]));
}

/** Evaluates achievement rules and stores new unlocks. Returns the new ones. */
export async function evaluateAchievements(learnerId: string): Promise<string[]> {
  const [completed, attempts, mastery, existing] = await Promise.all([
    db.sessions.where('[learnerId+status]').equals([learnerId, 'completed']).toArray(),
    db.attempts.where('learnerId').equals(learnerId).toArray(),
    db.mastery.where('learnerId').equals(learnerId).toArray(),
    db.achievements.where('learnerId').equals(learnerId).toArray(),
  ]);
  const owned = new Set(existing.map((a) => a.achievementId));
  const unlockedAt = nowIso();
  const fresh: LearnerAchievement[] = unlockedIds({ completed, attempts, mastery })
    .filter((id) => !owned.has(id))
    .map((achievementId) => ({ learnerId, achievementId, unlockedAt }));
  if (fresh.length > 0) await safeWrite(() => db.achievements.bulkAdd(fresh));
  return fresh.map((a) => a.achievementId);
}

export interface FinishResult {
  session: LearningSession;
  newAchievements: string[];
  mastery: MasteryRecord[];
}

export async function finishLearningSession(
  sessionId: string,
  result: { score: number; stars: number },
): Promise<FinishResult> {
  const session = await db.sessions.get(sessionId);
  if (!session) throw new Error('Session not found');
  const completed: LearningSession = {
    ...session,
    status: 'completed',
    endedAt: nowIso(),
    score: result.score,
    stars: result.stars,
  };
  await safeWrite(() => db.sessions.put(learningSessionSchema.parse(completed)));
  const mastery = await refreshMastery(session.learnerId, session.objectiveIds);
  const newAchievements = await evaluateAchievements(session.learnerId);
  return { session: completed, newAchievements, mastery };
}

/**
 * Sessions interrupted by closing the app stay "in progress". Their answers are
 * real evidence, so they are kept; the session is closed as abandoned.
 */
export async function recoverInterruptedSessions(
  now = Date.now(),
  olderThanMs = ABANDON_AFTER_MS,
): Promise<number> {
  const stale = (await db.sessions.where('status').equals('inProgress').toArray()).filter(
    (s) => now - new Date(s.startedAt).getTime() > olderThanMs,
  );
  for (const session of stale) {
    const attempts = await db.attempts.where('sessionId').equals(session.id).toArray();
    const last = attempts
      .map((a) => a.answeredAt)
      .sort()
      .at(-1);
    await db.sessions.put({ ...session, status: 'abandoned', endedAt: last ?? session.startedAt });
    await refreshMastery(session.learnerId, session.objectiveIds);
  }
  return stale.length;
}

/** Abandons one session immediately (the child left the game). */
export async function abandonSession(sessionId: string): Promise<void> {
  const session = await db.sessions.get(sessionId);
  if (!session || session.status !== 'inProgress') return;
  await safeWrite(() => db.sessions.put({ ...session, status: 'abandoned', endedAt: nowIso() }));
  await refreshMastery(session.learnerId, session.objectiveIds);
}

/** Exercise keys answered recently, to avoid showing the same questions again. */
export async function recentExerciseKeys(learnerId: string, limit = 60): Promise<Set<string>> {
  const recent = await db.attempts
    .where('[learnerId+answeredAt]')
    .between([learnerId, ''], [learnerId, '￿'])
    .reverse()
    .limit(limit)
    .toArray();
  return new Set(recent.map((a) => a.exerciseKey));
}

export async function sessionsOf(learnerId: string): Promise<LearningSession[]> {
  return db.sessions
    .where('[learnerId+startedAt]')
    .between([learnerId, ''], [learnerId, '￿'])
    .toArray();
}

export async function attemptsOf(learnerId: string): Promise<ExerciseAttempt[]> {
  return db.attempts.where('learnerId').equals(learnerId).toArray();
}
